import * as Sentry from '@sentry/react';
const FIRST_PARTY_SPAN_HOST = /(^|\.)(sayanchor\.com|anchor\.sh)$/i;
const requestHostname = (url)=>{
    const absolute = url.startsWith('//') ? "https:".concat(url) : url;
    try {
        return new URL(absolute).hostname;
    } catch (unused) {
        return null;
    }
};
export const shouldCreateSpanForRequest = (url)=>{
    const hostname = requestHostname(url);
    return hostname == null || FIRST_PARTY_SPAN_HOST.test(hostname);
};
export const THIRD_PARTY_RESOURCE_SPAN = /^(?:[A-Z]+ )?https?:\/\/(?!(?:[^/?#]*\.)?(?:sayanchor\.com|anchor\.sh)(?:[:/?#]|$))/i;
export const HIGH_VALUE_TRANSACTION_SAMPLE_RATE = 1;
export const LOW_VALUE_TRANSACTION_SAMPLE_RATE = 0.05;
export const DEFAULT_TRANSACTION_SAMPLE_RATE = 0.1;
const HIGH_VALUE_TRANSACTION = /^\/(?:engagement-letter|notification-action|notification|draft|edit-agreement|agreement-studio|signup)(?:\/|$)/i;
const LOW_VALUE_TRANSACTION = /^\/home(?:\/dashboard)?\/?$/i;
export const sampleRateForTransaction = (name)=>{
    if (HIGH_VALUE_TRANSACTION.test(name)) return HIGH_VALUE_TRANSACTION_SAMPLE_RATE;
    if (LOW_VALUE_TRANSACTION.test(name)) return LOW_VALUE_TRANSACTION_SAMPLE_RATE;
    return DEFAULT_TRANSACTION_SAMPLE_RATE;
};
export const beforeSendFilter = (event)=>{
    var _event_exception;
    const exceptions = (_event_exception = event.exception) === null || _event_exception === void 0 ? void 0 : _event_exception.values;
    if ((exceptions === null || exceptions === void 0 ? void 0 : exceptions.length) && !exceptions.some((e)=>{
        var _ref;
        var _e_stacktrace_frames, _e_stacktrace;
        return ((_ref = (_e_stacktrace = e.stacktrace) === null || _e_stacktrace === void 0 ? void 0 : (_e_stacktrace_frames = _e_stacktrace.frames) === null || _e_stacktrace_frames === void 0 ? void 0 : _e_stacktrace_frames.length) !== null && _ref !== void 0 ? _ref : 0) > 0;
    })) {
        return null;
    }
    return event;
};
const chainBelowAncestor = (ancestor, node)=>node == null || node === ancestor ? [] : [
        ...chainBelowAncestor(ancestor, node.parentElement),
        node.tagName.toLowerCase()
    ];
export const beforeBreadcrumbFilter = (breadcrumb, hint)=>{
    var _hint_event;
    if (breadcrumb.category !== 'ui.click' && breadcrumb.category !== 'ui.input') {
        return breadcrumb;
    }
    const target = hint === null || hint === void 0 ? void 0 : (_hint_event = hint.event) === null || _hint_event === void 0 ? void 0 : _hint_event.target;
    if (!(target instanceof HTMLElement)) {
        return breadcrumb;
    }
    const tagged = target.closest('[data-testid]:not([data-testid=""])');
    const testId = tagged instanceof HTMLElement ? tagged.dataset.testid : undefined;
    if (tagged instanceof HTMLElement && testId) {
        const chain = chainBelowAncestor(tagged, target);
        breadcrumb.message = chain.length ? "data-testid=".concat(testId, " > ").concat(chain.join(' > ')) : "data-testid=".concat(testId);
    }
    return breadcrumb;
};
export const initSentry = (history)=>{
    if (!window.sentryEnvironment) return;
    Sentry.init({
        dsn: 'https://ff94ed3a7d9149d3b85db4470ae506e1@o326813.ingest.sentry.io/1835583',
        environment: window.sentryEnvironment,
        integrations: [
            Sentry.reactRouterV5BrowserTracingIntegration({
                history,
                shouldCreateSpanForRequest
            }),
            // See: https://docs.sentry.io/platforms/javascript/configuration/filtering/#using-thirdpartyerrorfilterintegration
            Sentry.thirdPartyErrorFilterIntegration({
                filterKeys: [
                    'anchor-frontend-sentry-key'
                ],
                behaviour: 'apply-tag-if-contains-third-party-frames'
            })
        ],
        tracesSampler: (samplingContext)=>samplingContext.inheritOrSampleWith(sampleRateForTransaction(samplingContext.name)),
        ignoreSpans: [
            THIRD_PARTY_RESOURCE_SPAN
        ],
        tracePropagationTargets: [
            /(my|demo).anchor.sh/
        ],
        release: GIT_SHA1,
        denyUrls: [
            /^chrome-extension:\/\//i,
            /^safari-extension:\/\//i,
            /clarity\.js/i,
            /adsscriptloaderstatic/i,
            /\/\/hidden\//i,
            /\/appcues/i,
            /googletagmanager\.com/i,
            /this\.nexusWebSocket/i,
            /\/collectedforms\.js/i,
            /\/li.lms-analytics/i,
            /hotjar/i,
            /\/frame-/i,
            /google-analytics\.com\/analytics\.js/i,
            /translate\.googleapis\.com/i,
            /translate\.google\.com/i,
            /msDiscoverChatAvailable/i,
            /\/Applications\//,
            /\/persona\.js/,
            /js\.stripe\.com/,
            /@stripe\/stripe-js/,
            /intercom/,
            /maps\.googleapis\.com/,
            /\/parchment\//,
            /\/quill\.js/,
            /hs-analytics/,
            /material-ui\.js$/,
            /zapier\.com/
        ],
        ignoreErrors: [
            "Failed to execute 'sendBeacon' on 'Navigator'",
            /Network Error/i,
            /NetworkError/i,
            'Load failed',
            'Failed to fetch',
            /__gCrWeb/,
            /\[Parchment\]/i,
            /^AxiosError:/,
            'ResizeObserver loop limit exceeded',
            "Failed to execute 'removeChild' on 'Node'",
            "Can't find variable: gmo",
            "Cannot read properties of undefined (reading 'domInteractive')",
            "Can't find variable: lastScrollEventTimestamp"
        ],
        beforeSend: beforeSendFilter,
        beforeBreadcrumb: beforeBreadcrumbFilter
    });
    if (window._sessionId) {
        Sentry.setTag('sessionId', window._sessionId);
    }
};
